import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  Clock,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Wrench,
  Calendar,
  Target,
  Gauge,
} from 'lucide-react';
import apiClient from '../../services/api-client';

interface MaintenanceKPIs {
  mtbf_days: number; // Mean Time Between Failures
  mttr_hours: number; // Mean Time To Repair
  availability_percent: number;
  oee_percent: number; // Overall Equipment Effectiveness
  preventive_compliance: number; // % of preventive tasks done on time
  corrective_ratio: number; // % of corrective vs total work orders
  total_work_orders: number;
  completed_work_orders: number;
  avg_response_time_hours: number;
  total_downtime_hours: number;
  total_maintenance_cost: number;
  cost_per_equipment: number;
}

export default function MaintenanceKPIsDashboard() {
  const { t } = useTranslation();

  // Fetch KPIs from work orders and preventive tasks
  const { data: kpis, isLoading } = useQuery({
    queryKey: ['maintenance-kpis'],
    queryFn: async () => {
      // Fetch work orders and preventive tasks to calculate KPIs
      const [woRes, tasksRes] = await Promise.all([
        apiClient.get('/work-orders', { params: { limit: 500 } }),
        apiClient.get('/preventive-tasks', { params: { limit: 500 } }),
      ]);

      const workOrders = woRes.data.items || [];
      const tasks = tasksRes.data.items || [];

      // Calculate KPIs
      const completedWOs = workOrders.filter((wo: any) => wo.status === 'completed');
      const correctiveWOs = workOrders.filter((wo: any) => wo.type === 'corrective');
      const completedTasks = tasks.filter((t: any) => t.status === 'completed');
      const onTimeTasks = completedTasks.filter((t: any) => {
        if (!t.completed_at || !t.due_date) return false;
        return new Date(t.completed_at) <= new Date(t.due_date);
      });

      // Total downtime hours from completed work orders
      const totalDowntime = completedWOs.reduce((sum: number, wo: any) => {
        return sum + (wo.actual_duration_hours || 0);
      }, 0);

      // Total maintenance cost
      const totalCost = workOrders.reduce((sum: number, wo: any) => {
        return sum + (wo.actual_labor_cost || 0) + (wo.actual_parts_cost || 0);
      }, 0);

      // Calculate MTTR (Mean Time To Repair)
      const mttr = completedWOs.length > 0 ? totalDowntime / completedWOs.length : 0;

      // Calculate MTBF (based on corrective work orders - failures)
      const failureCount = correctiveWOs.filter((wo: any) => wo.status === 'completed').length;
      const operationalHours = 365 * 24; // Assume 1 year of operation
      const mtbf = failureCount > 0 ? (operationalHours - totalDowntime) / failureCount / 24 : operationalHours / 24;

      // Calculate availability
      const availability = operationalHours > 0 ? ((operationalHours - totalDowntime) / operationalHours) * 100 : 100;

      // Preventive compliance rate
      const preventiveCompliance = tasks.length > 0 ? (onTimeTasks.length / tasks.length) * 100 : 100;

      // Corrective ratio
      const correctiveRatio = workOrders.length > 0 ? (correctiveWOs.length / workOrders.length) * 100 : 0;

      // Average response time (from creation to start)
      const woWithStart = workOrders.filter((wo: any) => wo.actual_start && wo.created_at);
      const avgResponseTime = woWithStart.length > 0
        ? woWithStart.reduce((sum: number, wo: any) => {
            const start = new Date(wo.actual_start).getTime();
            const created = new Date(wo.created_at).getTime();
            return sum + (start - created) / (1000 * 60 * 60);
          }, 0) / woWithStart.length
        : 0;

      // OEE (simplified: Availability × Performance × Quality)
      // Using availability and preventive compliance as proxies
      const oee = (availability / 100) * (preventiveCompliance / 100) * 0.95 * 100;

      return {
        mtbf_days: mtbf,
        mttr_hours: mttr,
        availability_percent: Math.min(availability, 100),
        oee_percent: Math.min(oee, 100),
        preventive_compliance: preventiveCompliance,
        corrective_ratio: correctiveRatio,
        total_work_orders: workOrders.length,
        completed_work_orders: completedWOs.length,
        avg_response_time_hours: avgResponseTime,
        total_downtime_hours: totalDowntime,
        total_maintenance_cost: totalCost,
        cost_per_equipment: totalCost / Math.max(1, workOrders.length),
      } as MaintenanceKPIs;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="animate-pulse bg-gray-200 dark:bg-gray-700 rounded-lg h-24" />
        ))}
      </div>
    );
  }

  if (!kpis) return null;

  const getAvailabilityColor = (value: number) => {
    if (value >= 95) return 'text-green-600 dark:text-green-400';
    if (value >= 85) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getComplianceColor = (value: number) => {
    if (value >= 90) return 'text-green-600 dark:text-green-400';
    if (value >= 70) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  return (
    <div className="space-y-6">
      {/* Primary KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        {/* MTBF */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
            <Activity className="w-4 h-4 text-blue-500" />
            {t('kpis.mtbf', 'MTBF')}
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {kpis.mtbf_days.toFixed(0)}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {t('kpis.days', 'jours')}
          </div>
        </div>

        {/* MTTR */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
            <Clock className="w-4 h-4 text-orange-500" />
            {t('kpis.mttr', 'MTTR')}
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {kpis.mttr_hours.toFixed(1)}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {t('kpis.hours', 'heures')}
          </div>
        </div>

        {/* Availability */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
            <Gauge className="w-4 h-4 text-green-500" />
            {t('kpis.availability', 'Disponibilité')}
          </div>
          <div className={`text-2xl font-bold ${getAvailabilityColor(kpis.availability_percent)}`}>
            {kpis.availability_percent.toFixed(1)}%
          </div>
          <div className="flex items-center gap-1 text-xs">
            {kpis.availability_percent >= 95 ? (
              <TrendingUp className="w-3 h-3 text-green-500" />
            ) : (
              <TrendingDown className="w-3 h-3 text-red-500" />
            )}
            <span className="text-gray-500 dark:text-gray-400">
              {kpis.availability_percent >= 95 ? t('kpis.excellent', 'Excellent') : t('kpis.needsImprovement', 'À améliorer')}
            </span>
          </div>
        </div>

        {/* OEE */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
            <Target className="w-4 h-4 text-purple-500" />
            {t('kpis.oee', 'OEE')}
          </div>
          <div className={`text-2xl font-bold ${getAvailabilityColor(kpis.oee_percent)}`}>
            {kpis.oee_percent.toFixed(1)}%
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {t('kpis.oeeDescription', 'Efficacité globale')}
          </div>
        </div>

        {/* Preventive Compliance */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
            <Calendar className="w-4 h-4 text-cyan-500" />
            {t('kpis.preventiveCompliance', 'Conformité MP')}
          </div>
          <div className={`text-2xl font-bold ${getComplianceColor(kpis.preventive_compliance)}`}>
            {kpis.preventive_compliance.toFixed(0)}%
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {t('kpis.onTime', 'à temps')}
          </div>
        </div>

        {/* Corrective Ratio */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
            <Wrench className="w-4 h-4 text-red-500" />
            {t('kpis.correctiveRatio', 'Ratio correctif')}
          </div>
          <div className={`text-2xl font-bold ${kpis.corrective_ratio <= 30 ? 'text-green-600' : kpis.corrective_ratio <= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
            {kpis.corrective_ratio.toFixed(0)}%
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {kpis.corrective_ratio <= 30 ? (
              <span className="flex items-center gap-1 text-green-600">
                <CheckCircle className="w-3 h-3" />
                {t('kpis.good', 'Bon')}
              </span>
            ) : (
              <span className="flex items-center gap-1 text-orange-600">
                <AlertTriangle className="w-3 h-3" />
                {t('kpis.highCorrective', 'Trop de correctif')}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Secondary KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {/* Total Work Orders */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg p-4">
          <div className="text-sm text-blue-700 dark:text-blue-300 mb-1">
            {t('kpis.totalWorkOrders', 'Total OT')}
          </div>
          <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
            {kpis.total_work_orders}
          </div>
          <div className="text-xs text-blue-600 dark:text-blue-400">
            {kpis.completed_work_orders} {t('kpis.completed', 'terminés')}
          </div>
        </div>

        {/* Average Response Time */}
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 rounded-lg p-4">
          <div className="text-sm text-orange-700 dark:text-orange-300 mb-1">
            {t('kpis.avgResponseTime', 'Temps de réponse')}
          </div>
          <div className="text-2xl font-bold text-orange-900 dark:text-orange-100">
            {kpis.avg_response_time_hours.toFixed(1)}h
          </div>
          <div className="text-xs text-orange-600 dark:text-orange-400">
            {t('kpis.average', 'en moyenne')}
          </div>
        </div>

        {/* Total Downtime */}
        <div className="bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 rounded-lg p-4">
          <div className="text-sm text-red-700 dark:text-red-300 mb-1">
            {t('kpis.totalDowntime', 'Temps d\'arrêt total')}
          </div>
          <div className="text-2xl font-bold text-red-900 dark:text-red-100">
            {kpis.total_downtime_hours.toFixed(0)}h
          </div>
          <div className="text-xs text-red-600 dark:text-red-400">
            {(kpis.total_downtime_hours / 24).toFixed(1)} {t('kpis.days', 'jours')}
          </div>
        </div>

        {/* Total Maintenance Cost */}
        <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-lg p-4">
          <div className="text-sm text-green-700 dark:text-green-300 mb-1">
            {t('kpis.maintenanceCost', 'Coût maintenance')}
          </div>
          <div className="text-2xl font-bold text-green-900 dark:text-green-100">
            {(kpis.total_maintenance_cost / 1000).toFixed(1)}k
          </div>
          <div className="text-xs text-green-600 dark:text-green-400">
            TND
          </div>
        </div>
      </div>

      {/* KPI Targets Indicator */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
        <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
          {t('kpis.targetIndicators', 'Indicateurs de performance')}
        </h3>
        <div className="space-y-3">
          {/* Availability Bar */}
          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-gray-600 dark:text-gray-400">{t('kpis.availability', 'Disponibilité')}</span>
              <span className={getAvailabilityColor(kpis.availability_percent)}>
                {kpis.availability_percent.toFixed(1)}% / 95%
              </span>
            </div>
            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${
                  kpis.availability_percent >= 95 ? 'bg-green-500' : kpis.availability_percent >= 85 ? 'bg-yellow-500' : 'bg-red-500'
                }`}
                style={{ width: `${Math.min(kpis.availability_percent, 100)}%` }}
              />
            </div>
          </div>

          {/* Preventive Compliance Bar */}
          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-gray-600 dark:text-gray-400">{t('kpis.preventiveCompliance', 'Conformité MP')}</span>
              <span className={getComplianceColor(kpis.preventive_compliance)}>
                {kpis.preventive_compliance.toFixed(0)}% / 90%
              </span>
            </div>
            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${
                  kpis.preventive_compliance >= 90 ? 'bg-green-500' : kpis.preventive_compliance >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                }`}
                style={{ width: `${Math.min(kpis.preventive_compliance, 100)}%` }}
              />
            </div>
          </div>

          {/* Corrective Ratio Bar (inverse - lower is better) */}
          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-gray-600 dark:text-gray-400">{t('kpis.correctiveRatio', 'Ratio correctif')}</span>
              <span className={kpis.corrective_ratio <= 30 ? 'text-green-600' : kpis.corrective_ratio <= 50 ? 'text-yellow-600' : 'text-red-600'}>
                {kpis.corrective_ratio.toFixed(0)}% / &lt;30%
              </span>
            </div>
            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${
                  kpis.corrective_ratio <= 30 ? 'bg-green-500' : kpis.corrective_ratio <= 50 ? 'bg-yellow-500' : 'bg-red-500'
                }`}
                style={{ width: `${Math.min(kpis.corrective_ratio, 100)}%` }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
